<script lang="ts" setup>
import { useUiLayoutStore } from '~/store'
import { useVideos } from '~/pages/goods/composables/ui'
import { useGoodsStore } from '~/store/store-goods/goods'
import { useDisplayInfo } from '~/composable/common/goods/use-display-info'
import { commaizeNumber } from '@/utils/primitive/number'

export interface Props {
  stopWatch: string
  broadcastTimeInfo?: BroadCastTimeInfo | null
  liveOrdQty: number
}

type BroadCastTimeInfo = {
  percent: number
}

const props = withDefaults(defineProps<Props>(), {
  stopWatch: ''
})

// variables
const uiLayoutStore = useUiLayoutStore()
const goodsStore = useGoodsStore()
const { goods } = storeToRefs(goodsStore)

const isShowArea = ref<boolean>(true)

const { isVideoArea, hideVideoNav, checkVideoNav } = useVideos()
const isShowGoodsPlaying = computed(() => {
  if (goods.value?.brdctInfo?.liveCd === 'A') {
    return !isVideoArea.value
  }
})

const percent = computed(() => props.broadcastTimeInfo?.percent)
const progressValue = computed(() => {
  if (percent.value) {
    return percent.value
  } else {
    return 0
  }
})

const dispTypeCd = computed(() => goods.value?.detailInfo?.dispTypeCd)
const channelName = computed(() => {
  if (goods.value?.detailInfo?.busChnId === 'TV') {
    return 'TV LIVE'
  } else if (goods.value?.detailInfo?.busChnId === 'CTCOM') {
    return 'TV Shop+'
  }
})
const { displayInfo } = useDisplayInfo(dispTypeCd.value as string)

/** 구매중인 수량 */
const liveOrderQty = computed(() => props.liveOrdQty)
const liveOrderContent = computed(() => {
  if (!displayInfo.value.isFormless) {
    return '개 구매 중'
  } else {
    return '개 상담 신청 중'
  }
})

watch(
  () => props.stopWatch,
  count => {
    if (count === '00:00:00') {
      _onCloseVideoNav()
    }
  }
)

watch(
  () => isVideoArea.value,
  newVal => {
    if (!newVal) {
      isShowArea.value = true
    }
  }
)

// hooks
onMounted(() => {
  isShowArea.value = false
  window.addEventListener('scroll', checkVideoNav)
})
onUnmounted(() => {
  window.removeEventListener('scroll', checkVideoNav)
})

// functions
const _onCloseVideoNav = () => {
  isShowArea.value = false
  hideVideoNav()
  uiLayoutStore.resetHeaderClass()
}
const _onGoTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <div class="goods-playing-wrap" v-if="isShowGoodsPlaying && isShowArea && stopWatch !== '00:00:00'">
    <div class="goods-playing-inner">
      <div class="goods-wrap" @click="_onGoTop">
        <div class="goods-info">
          <div :class="['play-time-wrap', goods?.detailInfo?.busChnId === 'CTCOM' && 'shop-plus']">
            <ul class="live-wrap">
              <li class="real">방송중</li>
              <li class="real">{{ channelName }}</li>
            </ul>

            <span class="play-time">{{ stopWatch }}</span>
            <span class="purchase-num" v-if="liveOrderQty > 0"
              ><em class="num">+{{ commaizeNumber(liveOrderQty) }}</em
              >{{ liveOrderContent }}</span
            >
          </div>

          <strong class="goods-name">{{ goods?.detailInfo?.productNm }}</strong>
        </div>
      </div>
      <div class="play-progress-wrap">
        <progress :value="progressValue" max="100">{{ progressValue }}%</progress>
      </div>
      <button type="button" class="btn-close" @click="_onCloseVideoNav">
        <span class="blind">닫기</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '~/pages/goods/scss/videos-playing.scss';
</style>
